import React from "react";
import { DateTime } from "luxon";
import currency from "currency.js";
import type { Payment, Invitation } from '@app-types';
import { Badge } from '@common';
import type { BadgeColor } from '@common';

interface PaymentWithName extends Payment {
  fullName: string;
}

interface Props {
  invitations: Invitation[];
  statusEnum: {
    refunded: number;
    voided: number;
  };
}

type PaymentGroupKey = `autopay` | `refunded` | `voided`;

const paymentGroupKeys: PaymentGroupKey[] = [`autopay`, `refunded`, `voided`];

const GroupedPayments: React.FC<Props> = ({ invitations, statusEnum }) => {
  const uniqueId = React.useId();
  const groupedPayments: Record<PaymentGroupKey, PaymentWithName[]> = {
    autopay: [],
    refunded: [],
    voided: []
  };

  invitations.forEach(invitation => {
    invitation.payments.forEach(payment => {
      const paymentWithName: PaymentWithName = { ...payment, fullName: invitation.fullName };

      if (payment.idTransactionStatus === statusEnum.refunded) {
        groupedPayments.refunded.push(paymentWithName);
      } else if (payment.idTransactionStatus === statusEnum.voided) {
        groupedPayments.voided.push(paymentWithName);
      } else if (payment.isAutopayment) {
        groupedPayments.autopay.push(paymentWithName);
      }
    });
  });

  const badgeMap: Record<PaymentGroupKey, { label: string; color: BadgeColor }> = {
    autopay: { label: `Autopay`, color: `green` },
    refunded: { label: `Refunded`, color: `red` },
    voided: { label: `Voided`, color: `orange` }
  };

  // Return null if there are no payments to display
  const hasPayments = Object.values(groupedPayments).some(group => group.length > 0);
  if (!hasPayments) {
    return null;
  }

  return (
    <div className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-2">
      {paymentGroupKeys.map((key) => {
        const group = groupedPayments[key];
        if (!group.length) return null;

        const badgeId = `${uniqueId}-payment-group-${key}`;

        return (
          <React.Fragment key={key}>
            {/* Badge */}
            <Badge
              id={badgeId}
              role="heading"
              aria-level={3}
              color={badgeMap[key].color}
              className="self-start"
            >
              {badgeMap[key].label}
            </Badge>

            {/* Payment Items */}
            <div className="flex flex-col gap-2 min-w-0" role="group" aria-labelledby={badgeId}>
              {group.map(payment => {
                const {
                  idPayment,
                  fullName,
                  createdAt,
                  amount,
                  processingFeeAmount
                } = payment;

                const displayDate = DateTime.fromISO(createdAt).toLocaleString({ year: `numeric`, month: `2-digit`, day: `2-digit` });
                const total = currency(amount).add(processingFeeAmount);

                return (
                  <div
                    key={idPayment}
                    className="flex items-center gap-6 text-sm leading-5 text-neutral-800 tracking-[0.14px]"
                  >
                    <dt className="flex-1 min-w-0 overflow-hidden text-ellipsis whitespace-nowrap">
                      {`${displayDate}, ${fullName}`}
                    </dt>
                    <dd className="shrink-0 text-right">{total.format()}</dd>
                  </div>
                );
              })}
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
};

export default GroupedPayments;
